import { FC, useRef } from 'react';
import { Box, Button, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { useTrans } from '@libs/i18n';
import { SparkleIcon, UploadSimpleIcon } from '@phosphor-icons/react';

import {
  TENANT_PROFILE_ALLOWED_FILE_TYPES,
  useTenantProfileUpload,
} from '../../upload/TenantProfileUploadProvider';
import { ORG_TOKENS } from '../utils/tokens';

/**
 * Global "upload anything" bar. Drop files anywhere on the page, or click
 * "Upload files" to browse — both feed the shared upload provider, which runs
 * the three-step upload; confirm invalidates the documents + summary queries so
 * the category tabs/summary refresh and the busy-poll drives the
 * Classifying → Extracting → Pending-review transitions automatically.
 * Classification/routing — and auto-extraction on a valid verdict — happen
 * server-side; no Extract click is required.
 */
export const GlobalUploadBar: FC = observer(() => {
  const { t } = useTrans();
  const theme = useTheme();
  const { addFiles } = useTenantProfileUpload();
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'flex-start', sm: 'center' },
        justifyContent: 'space-between',
        gap: { xs: 1.5, sm: 2 },
        px: { xs: 1.5, sm: 2 },
        py: 1.5,
        borderRadius: `${ORG_TOKENS.radiusMd}px`,
        border: 1,
        borderColor: 'grey.400',
        backgroundColor: 'background.paper',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
        <UploadSimpleIcon size={22} color={theme.palette.text.tertiary} />
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="textSM_semiBold" color="text.primary">
              {t('app.orgProfile.upload.title')}
            </Typography>
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.4,
                typography: 'textXS_semiBold',
                color: 'blue.700',
                backgroundColor: 'blue.100',
                border: 1,
                borderColor: 'blue.200',
                borderRadius: 999,
                px: 0.75,
                py: '1px',
              }}
            >
              <SparkleIcon size={10} weight="fill" />
              {t('app.orgProfile.upload.aiRouted')}
            </Box>
          </Box>
          <Typography variant="textXS_regular" color="text.tertiary">
            {t('app.orgProfile.upload.subtitle')}
          </Typography>
        </Box>
      </Box>

      <input
        type="file"
        ref={inputRef}
        multiple
        accept={TENANT_PROFILE_ALLOWED_FILE_TYPES.join(',')}
        style={{ display: 'none' }}
        aria-label={t('app.orgProfile.upload.button')}
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          if (files.length > 0) addFiles(files);
          e.target.value = '';
        }}
      />

      <Button
        variant="contained"
        startIcon={<UploadSimpleIcon size={16} />}
        onClick={() => inputRef.current?.click()}
        sx={{ textTransform: 'none', flex: 'none', width: { xs: '100%', sm: 'auto' } }}
      >
        {t('app.orgProfile.upload.button')}
      </Button>
    </Box>
  );
});
